import { createElement } from 'react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/lib/trpc/router';
import { ReferenceUnresolvedContent } from './ReferenceUnresolvedContent';
import { mapCuratedRecordToSourceContent } from './referenceCuratedRecordMapper';
import type { FacesheetSourceContent } from './types';

type GetPatientFacesheetReferenceDetailResponse =
  inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetReferenceDetail'];

export type MappedReferenceDetail = {
  source: FacesheetSourceContent;
  targetPage?: number;
  title: string;
  referenceDomain?: string | null;
};

const buildUnresolvedSource = (
  reason?: string,
  message?: string | null,
): FacesheetSourceContent => ({
  kind: 'template',
  render: () =>
    createElement(ReferenceUnresolvedContent, {
      reason,
      message,
    }),
});

export const mapReferenceDetailToViewerContent = (
  detail: GetPatientFacesheetReferenceDetailResponse,
): MappedReferenceDetail => {
  const signedUrl = detail.document?.signedUrl;
  if (signedUrl) {
    return {
      source: { kind: 'pdf', signedUrl },
      targetPage: detail.document?.pageNumber ?? undefined,
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  if (detail.curatedRecord) {
    return {
      source: mapCuratedRecordToSourceContent(
        detail.curatedRecord,
        detail.title,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  if (detail.unresolved) {
    return {
      source: buildUnresolvedSource(
        detail.unresolved.reason,
        detail.unresolved.message,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  return {
    source: buildUnresolvedSource(
      'missingContent',
      'No reference content is available for this item.',
    ),
    title: detail.title,
    referenceDomain: detail.referenceDomain,
  };
};
